import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Container,
  Card,
  CardContent,
  Avatar,
  Typography,
  Button,
  Box,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import SecurityIcon from '@material-ui/icons/Security';
import HelpIcon from '@material-ui/icons/Help';
import NewReleasesIcon from '@material-ui/icons/NewReleases';
import { Settings, ExitToApp } from '@material-ui/icons';
import { useNavigate } from 'react-router-dom';

// CAT Brand Colors
const CAT_YELLOW = '#FFCD11';
const CAT_BLACK = '#1A1A1A';
const CAT_DARK_GRAY = '#3D3D3D';
const CAT_MID_GRAY = '#6B6B6B';
const CAT_LIGHT_GRAY = '#F4F4F4';
const CAT_BORDER = '#E0E0E0';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: CAT_LIGHT_GRAY,
    minHeight: '100vh',
    padding: theme.spacing(4, 0),
  },
  container: {
    maxWidth: 860,
  },
  headerCard: {
    background: CAT_BLACK,
    color: 'white',
    marginBottom: theme.spacing(3),
    borderRadius: 10,
    boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
  },
  profileSection: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(3, 4),
    gap: theme.spacing(3),
  },
  avatar: {
    width: theme.spacing(11),
    height: theme.spacing(11),
    marginRight: 0,
    fontSize: '2rem',
    fontWeight: 700,
    backgroundColor: CAT_YELLOW,
    color: CAT_BLACK,
    border: `3px solid ${CAT_YELLOW}`,
  },
  profileInfo: {
    flex: 1,
  },
  profileName: {
    fontSize: '1.6rem',
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
    color: '#FFFFFF',
    letterSpacing: '-0.3px',
  },
  profileEmail: {
    fontSize: '0.95rem',
    color: 'rgba(255,255,255,0.7)',
    marginBottom: theme.spacing(0),
  },
  signOutButton: {
    backgroundColor: CAT_YELLOW,
    color: CAT_BLACK,
    border: 'none',
    '&:hover': {
      backgroundColor: '#e6b800',
    },
    padding: theme.spacing(1, 2.5),
    borderRadius: 6,
    fontWeight: 700,
    textTransform: 'none',
    fontSize: '0.9rem',
    whiteSpace: 'nowrap',
  },
  settingsCard: {
    marginBottom: theme.spacing(2.5),
    borderRadius: 10,
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
    border: `1px solid ${CAT_BORDER}`,
    backgroundColor: '#FFFFFF',
  },
  cardTitle: {
    fontSize: '0.8rem',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: CAT_MID_GRAY,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    borderLeft: `3px solid ${CAT_YELLOW}`,
    paddingLeft: theme.spacing(1),
  },
  cardContent: {
    padding: theme.spacing(3),
    '&:last-child': {
      paddingBottom: theme.spacing(3),
    },
  },
  settingItem: {
    padding: theme.spacing(1.75, 0),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '&:not(:last-child)': {
      borderBottom: `1px solid ${CAT_BORDER}`,
    },
  },
  settingLabel: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  settingText: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
  },
  settingTitle: {
    fontWeight: 600,
    color: CAT_DARK_GRAY,
    fontSize: '0.95rem',
  },
  settingDescription: {
    fontSize: '0.85rem',
    color: CAT_MID_GRAY,
  },
  iconButton: {
    color: CAT_YELLOW,
    backgroundColor: 'rgba(255, 205, 17, 0.12)',
    borderRadius: '50%',
    padding: 6,
    fontSize: '1.7rem',
  },
  infoBox: {
    backgroundColor: 'rgba(255, 205, 17, 0.08)',
    border: `1px solid rgba(255, 205, 17, 0.35)`,
    borderRadius: 8,
    padding: theme.spacing(1.75),
    marginBottom: theme.spacing(2),
    color: CAT_DARK_GRAY,
  },
}));

interface UserInfo {
  name?: string;
  email?: string;
}

export default function CustomSettingsPage(){
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = React.useState<UserInfo | null>(null);

  React.useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const userName = identity.userEntityRef?.split('/')[1];
        setUserInfo({
          name: userName || 'User',
          email: userName ? `${userName}@cat.com` : 'user@example.com',
        });
      } catch (error) {
        console.error('Failed to fetch user info:', error);
        setUserInfo({
          name: 'User',
          email: 'user@example.com',
        });
      }
    };
    fetchUserInfo();
  }, [identityApi]);

  const handleSignOut = async () => {
    try {
      await identityApi.signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const formatName = (name?: string) => {
    if (!name) return name;
    return name
      .split(/[.\s-]+/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  };

  const getInitials = (name?: string) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
      <div style={{backgroundColor:'black', color: 'white', padding: '18px',borderLeft: '0.1px solid #535353', fontWeight:"700", fontSize:'30px'}}>Settings</div>
      <div>
        <Box className={classes.root}>
          <Container maxWidth="md" className={classes.container}>
            {/* Profile Header Card */}
            <Card className={classes.headerCard}>
              <div className={classes.profileSection}>
                <Avatar className={classes.avatar}>
                  {getInitials(userInfo?.name)}
                </Avatar>
                <Box className={classes.profileInfo}>
                  <Typography className={classes.profileName}>
                    {formatName(userInfo?.name) || 'Loading...'}
                  </Typography>
                  <Typography className={classes.profileEmail}>
                    {userInfo?.email || ''}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<ExitToApp />}
                  className={classes.signOutButton}
                  onClick={handleSignOut}
                >
                  Sign Out
                </Button>
              </div>
            </Card>

            {/* Account Info Card */}
            <Card className={classes.settingsCard}>
              <CardContent className={classes.cardContent}>
                <Typography className={classes.cardTitle}>
                  Account Information
                </Typography>

                <Box className={classes.settingItem}>
                  <Box className={classes.settingText}>
                    <Typography className={classes.settingTitle}>Username</Typography>
                    <Typography className={classes.settingDescription}>
                      {userInfo?.name}
                    </Typography>
                  </Box>
                </Box>

                <Box className={classes.settingItem}>
                  <Box className={classes.settingText}>
                    <Typography className={classes.settingTitle}>Email</Typography>
                    <Typography className={classes.settingDescription}>
                      {userInfo?.email}
                    </Typography>
                  </Box>
                </Box>

                <Box className={classes.settingItem}>
                  <Box className={classes.settingText}>
                    <Typography className={classes.settingTitle}>Account Status</Typography>
                    <Typography className={classes.settingDescription}>
                      Active
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Release Notes Card */}
            <Card className={classes.settingsCard}>
              <CardContent className={classes.cardContent}>
                <Typography className={classes.cardTitle}>
                  Release Notes
                </Typography>

                <Box className={classes.infoBox}>
                  <Typography variant="body2">
                    See what&apos;s new in the latest Service Catalog release. Latest: <strong>2026.06.29</strong>
                  </Typography>
                </Box>

                <List style={{ padding: 0 }}>
                  <ListItem
                    button
                    onClick={() => navigate('/release-notes')}
                    style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 10, paddingBottom: 10, borderRadius: 6 }}
                  >
                    <ListItemIcon style={{ minWidth: 44 }}>
                      <NewReleasesIcon className={classes.iconButton} />
                    </ListItemIcon>
                    <ListItemText
                      primary={<span style={{ fontWeight: 600, color: '#3D3D3D', fontSize: '0.95rem' }}>View Release Notes</span>}
                      secondary="Features, improvements, and fixes"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            {/* Help & Support Card */}
            <Card className={classes.settingsCard}>
              <CardContent className={classes.cardContent}>
                <Typography className={classes.cardTitle}>
                  Help & Support
                </Typography>

                <Box className={classes.infoBox}>
                  <Typography variant="body2">
                    Need help? Check out our documentation or contact support for assistance.
                  </Typography>
                </Box>

                <List style={{ padding: 0 }}>
                  <ListItem
                    button
                    component="a"
                    href="https://catdigital.atlassian.net/wiki/spaces/CD/pages/4601577738/Service+Catalog+Runbook"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 10, paddingBottom: 10, borderRadius: 6 }}
                  >
                    <ListItemIcon style={{ minWidth: 44 }}>
                      <HelpIcon className={classes.iconButton} />
                    </ListItemIcon>
                    <ListItemText
                      primary={<span style={{ fontWeight: 600, color: '#3D3D3D', fontSize: '0.95rem' }}>Documentation</span>}
                      secondary="Learn how to use the platform"
                    />
                  </ListItem>
                  <Divider style={{ margin: '4px 0' }} />
                  <ListItem
                    button
                    component="a"
                    href="mailto:teamheliosservicecatalogGroup@cat.com"
                    style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 10, paddingBottom: 10, borderRadius: 6 }}
                  >
                    <ListItemIcon style={{ minWidth: 44 }}>
                      <SecurityIcon className={classes.iconButton} />
                    </ListItemIcon>
                    <ListItemText
                      primary={<span style={{ fontWeight: 600, color: '#3D3D3D', fontSize: '0.95rem' }}>Contact Support</span>}
                      secondary="Get help from our support team"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            {/* Security & Privacy Card */}
            <Card className={classes.settingsCard}>
              <CardContent className={classes.cardContent}>
                <Typography className={classes.cardTitle}>
                  Security & Privacy
                </Typography>

                <List style={{ padding: 0 }}>
                  <ListItem
                    button
                    component="a"
                    href="https://www.caterpillar.com/en/legal-notices.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 10, paddingBottom: 10, borderRadius: 6 }}
                  >
                    <ListItemIcon style={{ minWidth: 44 }}>
                      <SecurityIcon className={classes.iconButton} />
                    </ListItemIcon>
                    <ListItemText
                      primary={<span style={{ fontWeight: 600, color: '#3D3D3D', fontSize: '0.95rem' }}>Legal Policy</span>}
                      secondary="Check our legal policy details"
                    />
                  </ListItem>
                  <Divider style={{ margin: '4px 0' }} />
                  <ListItem
                    button
                    component="a"
                    href="https://www.caterpillar.com/en/legal-notices/privacy-notice.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 10, paddingBottom: 10, borderRadius: 6 }}
                  >
                    <ListItemIcon style={{ minWidth: 44 }}>
                      <Settings className={classes.iconButton} />
                    </ListItemIcon>
                    <ListItemText
                      primary={<span style={{ fontWeight: 600, color: '#3D3D3D', fontSize: '0.95rem' }}>Privacy Policy</span>}
                      secondary="Review our privacy practices"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
           
          </Container>
        </Box>
      </div>
      </>
  );
};
